import axios from 'axios';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { convert } from 'html-to-text';
import { PiCheckCircleDuotone, PiHeartDuotone, PiShareNetworkDuotone, PiArrowCircleRightDuotone, PiMapPinDuotone, PiCoinsDuotone, PiCakeDuotone, PiMagnifyingGlass, PiFunnelSimple, PiCaretDownBold, PiTrashDuotone, PiXBold } from 'react-icons/pi';

import Pattern from 'elements/Pattern';
import PageHeading from 'elements/PageHeading';
// import TextArtifact from 'elements/TextArtifact';

import { getColorConfig } from 'utils/StyleUtils'
import { getItemByKeyValue } from 'utils/GenericUtils';

import { useConfig } from 'providers/ConfigProvider';
import { useAuth } from 'providers/AuthProvider';
import { toggleFavoriteJob, getFavoriteJobIds, FAVORITES_CHANGED_EVENT, syncFavoriteJobsToBackend } from 'utils/FavoriteUtils';

export default function JobListPage(props) {
    const [searchParams] = useSearchParams();
    const countyIdFromUrl = searchParams.get('countyId');
    const initialCountyId = countyIdFromUrl ? Number(countyIdFromUrl) : null;

    const [list, setList] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [showFavorites, setShowFavorites] = useState(false);
    const [showFilter, setShowFilter] = useState(false);

    const [selectedCountyId, setSelectedCountyId] = useState(initialCountyId);
    const [selectedSettlementId, setSelectedSettlementId] = useState(null);
    const [selectedJobAreaIds, setSelectedJobAreaIds] = useState([]);
    const [filterUnder18, setFilterUnder18] = useState(false);
    const [filterLatest, setFilterLatest] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState({
        countyId: initialCountyId,
        settlementId: null,
        jobAreaId: null,
        newJobAdvertisement: false,
        younger18: false,
    });
    const { userData } = useAuth();
    const latestRequestId = useRef(0);

    const fetchJobs = useCallback((params = {}) => {
        const savedFavorites = getFavoriteJobIds();
        const requestId = ++latestRequestId.current;

        const url = global.backingsEndpoint + 'jobAdvertisementControl/findJobAdvertisementByParameter';

        const body = {
            countyId: params.countyId ?? null,
            settlementId: params.settlementId ?? null,
            jobAreaId: params.jobAreaId ?? null,
            staticJobAreaId: null,
            newJobAdvertisement: params.newJobAdvertisement ?? false,
            younger18: params.younger18 ?? false,
            keys: params.keys ?? null,
        };

        setIsLoading(true);

        axios.post(url, body)
            .then((response) => {
                if (requestId !== latestRequestId.current) return;

                const jobs = Array.isArray(response.data) ? response.data : [];
                const backendFavoriteIds = jobs
                    .filter((job) => job.favorite)
                    .map((job) => job.jobAdvertisementId);

                const mergedFavoriteIds = [...new Set([...savedFavorites, ...backendFavoriteIds])];

                localStorage.setItem('favoriteJobs', JSON.stringify(mergedFavoriteIds));

                const updatedJobs = jobs.map((job) => ({
                    ...job,
                    isFavorite: mergedFavoriteIds.includes(job.jobAdvertisementId),
                }));

                setList(updatedJobs);
            })
            .catch((error) => {
                if (requestId === latestRequestId.current) {
                    console.error('Job search failed:', error);
                    setList([]);
                }
            })
            .finally(() => {
                if (requestId === latestRequestId.current) {
                    setIsLoading(false);
                }
            });
    }, []);

    const handleApplyFilter = () => {
        setAppliedFilters({
            countyId: selectedCountyId,
            settlementId: selectedSettlementId,
            jobAreaId: selectedJobAreaIds.length > 0 ? selectedJobAreaIds : null,
            newJobAdvertisement: filterLatest,
            younger18: filterUnder18,
        });

        setShowFilter(false);
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }, []);

    useEffect(() => {
        if (!userData) return;

        const syncAfterLogin = async () => {
            try {
                const syncedFavorites = await syncFavoriteJobsToBackend(true);

                setList((prevList) =>
                    prevList.map((job) => ({
                        ...job,
                        isFavorite: syncedFavorites.includes(job.jobAdvertisementId),
                    }))
                );
            } catch (error) {
                console.error('Favorite sync after login failed:', error);
            }
        };

        syncAfterLogin();
    }, [userData]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearchQuery(searchQuery.trim());
        }, 450);

        return () => window.clearTimeout(timeoutId);
    }, [searchQuery]);

    useEffect(() => {
        const nextCountyId = countyIdFromUrl ? Number(countyIdFromUrl) : null;

        setSelectedCountyId(nextCountyId);
        setSelectedSettlementId(null);
        setAppliedFilters((currentFilters) => {
            if (
                currentFilters.countyId === nextCountyId &&
                currentFilters.settlementId === null
            ) {
                return currentFilters;
            }

            return {
                ...currentFilters,
                countyId: nextCountyId,
                settlementId: null,
            };
        });
    }, [countyIdFromUrl]);

    useEffect(() => {
        fetchJobs({
            ...appliedFilters,
            keys: debouncedSearchQuery ? debouncedSearchQuery.split(/\s+/) : null,
        });
    }, [appliedFilters, debouncedSearchQuery, fetchJobs]);

    const toggleFavorite = async (jobId) => {
        try {
            const nextFavorites = await toggleFavoriteJob(jobId, !!userData);

            setList((prevList) =>
                prevList.map((job) => ({
                    ...job,
                    isFavorite: nextFavorites.includes(job.jobAdvertisementId),
                }))
            );
        } catch (error) {
            console.error('Favorite toggle failed:', error);
        }
    };

    useEffect(() => {
        const syncFavorites = () => {
            const savedFavorites = getFavoriteJobIds();

            setList((prevList) =>
                prevList.map((job) => ({
                    ...job,
                    isFavorite: savedFavorites.includes(job.jobAdvertisementId),
                }))
            );
        };

        window.addEventListener(FAVORITES_CHANGED_EVENT, syncFavorites);
        window.addEventListener('storage', syncFavorites);

        return () => {
            window.removeEventListener(FAVORITES_CHANGED_EVENT, syncFavorites);
            window.removeEventListener('storage', syncFavorites);
        };
    }, []);

    const visibleList = showFavorites
        ? list.filter((job) => job.isFavorite)
        : list;

    return (
        <div className={'relative w-full flex-1 flex flex-col items-center gradient-blue px-site py-site ' + (props.className || '')}>

            <Pattern />

            <div className='w-full max-w-boxed flex flex-col gap-16 z-10'>

                <PageHeading
                    title='Diákmunkák a Fürge Diáknál'
                    subTitle={
                        <div className='text-sm sm:text-base leading-relaxed text-white/80 space-y-2'>
                            <p>
                                Szűkítsd a keresési feltételeket a{' '}
                                <button
                                    type='button'
                                    onClick={() => setShowFilter((prev) => !prev)}
                                    className='inline align-middle hover:text-white transition'
                                >
                                    <PiFunnelSimple
                                        className={
                                            'inline-block align-middle rounded-md p-1 size-6 shrink-0 transition ' +
                                            (showFilter
                                                ? 'gradient-green text-white'
                                                : 'bg-white/80 text-gray-600')
                                        }
                                    />
                                </button>{' '}
                                ikonra kattintva.
                            </p>

                            <p>
                                Nézd meg az általad érdekesnek megjelölt diákmunkákat a{' '}
                                <button
                                    type='button'
                                    onClick={() => setShowFavorites((prev) => !prev)}
                                    className='inline align-middle hover:text-white transition'
                                >
                                    <PiHeartDuotone
                                        className={
                                            'inline-block align-middle rounded-md p-1 size-6 shrink-0 transition ' +
                                            (showFavorites
                                                ? 'gradient-red text-white'
                                                : 'bg-white/80 text-gray-600')
                                        }
                                    />
                                </button>{' '}
                                ikonra kattintva.
                            </p>
                        </div>
                    }
                />

                <SearchBar 
                    searchQuery={searchQuery} 
                    setSearchQuery={setSearchQuery} 
                    isLoading={isLoading}
                    resultCount={visibleList.length}
                    showFavorites={showFavorites} 
                    setShowFavorites={setShowFavorites}
                    showFilter={showFilter}
                    setShowFilter={setShowFilter}
                />

                <div className='flex flex-col gap-6 md:gap-8'>
                    {!isLoading && visibleList.length > 0 && visibleList.map((item, index) => (
                        <JobListItem
                            key={item.jobAdvertisementId || item.prettyUrl || index}
                            data={item}
                            onFavoriteToggle={toggleFavorite}
                        />
                    ))}

                    {isLoading && [...Array(4)].map((_, index) => (
                        <div
                            key={`job-skeleton-${index}`}
                            className='w-full h-[200px] bg-white/20 animate-pulse rounded-3xl'
                        />
                    ))}

                    {!isLoading && visibleList.length === 0 && (
                        <div className='w-full rounded-3xl bg-white/20 p-8 text-center text-white font-display text-xl'>
                            {showFavorites
                                ? 'Nincs a feltételeknek megfelelő kedvenc diákmunkád.'
                                : 'Nem találtunk a keresésnek megfelelő diákmunkát.'}
                        </div>
                    )}
                </div>

            </div>

            <FilterSidebar
                isOpen={showFilter}
                onClose={() => setShowFilter(false)}
                onApplyFilter={handleApplyFilter}
                selectedCountyId={selectedCountyId}
                onCountyChange={(nextCountyId) => {
                    setSelectedCountyId(nextCountyId);
                    setSelectedSettlementId(null);
                }}
                selectedSettlementId={selectedSettlementId}
                onSettlementChange={setSelectedSettlementId}
                selectedJobAreaIds={selectedJobAreaIds}
                onJobAreasChange={setSelectedJobAreaIds}
                filterUnder18={filterUnder18}
                setFilterUnder18={setFilterUnder18}
                filterLatest={filterLatest}
                setFilterLatest={setFilterLatest}
            />
        </div>
    );
}

export function JobListItem(props) {
    const { getCombo } = useConfig();
    const navigate = useNavigate();

    const color = getItemByKeyValue(getCombo('jobArea'), 'id', props.data.jobAreaId)?.color || 'blue';

    const getShareData = () => {
        return { 'url': '', 'title': '', 'text': '' };
    };

    const handleApply = (event) => {
        event.preventDefault();
        event.stopPropagation();

        navigate('/' + props.data.prettyUrl, {
            state: { applyJobId: props.data.jobAdvertisementId },
        });
    };

    return (
        <Link to={'/' + props.data.prettyUrl} disabled className='w-full h-auto md:h-[200px] flex flex-col md:flex-row rounded-3xl shadow-2xl transition hover:ring-8 ring-white/20 bg-white/50 overflow-hidden'>

            <div className='hidden xl:block aspect-video rounded-l-3xl bg-cover bg-center bg-no-repeat z-10' style={{ backgroundImage: 'url(' + global.backingsEndpoint + 'photoRepository/getImageBySize?size=ORIGINAL&key=' + props.data.galleryImagePhotoKey + ')' }}></div>

            <div className='flex-1 flex flex-col gap-4 bg-white rounded-3xl shadow-2xl xl:-ml-6 p-5 z-20'>

                <div className='w-full flex flex-col gap-0.5'>

                    <div>
                        <div className='font-display font-bold text-2xl lg:text-2xl leading-tight lg:leading-tight break-words'>
                            {props.data.positionName.split('/').map((part, index) => (
                                <span key={index}>
                                    {part}
                                    {index < props.data.positionName.split('/').length - 1 && <wbr />}
                                    {index < props.data.positionName.split('/').length - 1 && '/'}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className='flex gap-1'>
                        <PiMapPinDuotone className='text-sm' />
                        <div className='font-primary text-xs'>{props.data.countyName + ' vármegye, ' + props.data.settlementName}</div>
                    </div>

                </div>

                <div className='sm:flex-1 overflow-hidden justify-end flex flex-col gap-4'>

                    <div className='text-xs line-clamp-5 md:line-clamp-3'>{convert(props.data.description, {}).trim().replace(/(\r\n|\n|\r)/gm, ' ').replace(/\s\s+/g, ' ')}</div>

                    <div className={'flex flex-wrap gap-2 ' + (props.className || '')}>
                        <Tag label={props.data.jobAreaName} color={color} />
                        <Tag label={'Bruttó ' + props.data.grossSalaryPay} color='yellow' icon={<PiCoinsDuotone />} />
                        {props.data.under18 && <Tag label='18 éves kor alatt is' color='blue' icon={<PiCakeDuotone />} />}
                    </div>

                </div>

            </div>

            <div className='grid grid-cols-4 md:grid-cols-2 gap-4 rounded-b-3xl md:rounded-r-3xl p-4'>

                <Button hoverClassName='gradient-green' icon={<PiCheckCircleDuotone />} label='Jelentkezem' onClick={handleApply} />
                <Button hoverClassName='gradient-red' icon={<PiHeartDuotone />} label='Kedvenc' onClick={(e) => { e.preventDefault(); props.onFavoriteToggle?.(props.data.jobAdvertisementId); }} active={props.data.isFavorite} />
                <Button hoverClassName='gradient-yellow' icon={<PiShareNetworkDuotone />} label='Megosztom' onClick={(e) => { e.preventDefault(); navigator.share(getShareData()); }} />
                <Button hoverClassName='gradient-blue' icon={<PiArrowCircleRightDuotone />} label='Részletek' />

            </div>

        </Link>
    );
}

function Button(props) {
    return (
        <button type='button' onClick={props.onClick} aria-label={props.label} title={props.label} className={`relative aspect-square min-[520px]:aspect-auto md:aspect-square flex flex-col items-center justify-center rounded-2xl overflow-hidden bg-black/20 hover:bg-white/10 transition-all duration-300 group p-1.5
            ${props.active ? `${props.hoverClassName}`  : 'bg-black/20 hover:bg-white/10'}`}
        >
            <div className={`absolute inset-0 transition-opacity duration-300 rounded-2xl ${props.active ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'} ${props.hoverClassName}`} />

            <div className='relative w-full h-full flex items-center justify-center'>

                <div className={`absolute top-0 left-0 w-full h-full transition-opacity opacity-0 group-hover:opacity-100 rounded-xl ${props.hoverClassName || ''}`}></div>

                <div className='text-white text-4xl z-10 p-2'>{props.icon}</div>
                {/* <div className='text-white text-[11px] font-bold font-display z-10 pt-0.5'>{props.label}</div> */}
            </div>

        </button>
    );
}

function Tag(props) {
    return (
        <div className={'flex gap-1 lg:gap-2 items-center rounded-lg text-white px-3 py-1.5 shadow-lg ' + (getColorConfig(props.color).gradient) + ' ' + (props.className || '')}>

            {props.icon && <div>{props.icon}</div>}

            <div className='font-medium text-[10px] lg:text-xs whitespace-nowrap'>{props.label}</div>

        </div>
    );
}

function SearchBar(props) {
    return (
        <div className="w-full max-w-boxed z-10 flex justify-center">
            <div className="w-full">

                <div className="flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 p-2 shadow-lg">
                    <div className="min-w-0 flex-1 flex items-center gap-3 rounded-xl bg-white px-4 h-14">
                        <PiMagnifyingGlass className="text-xl text-sky-600 shrink-0" />

                        <label htmlFor="job-search" className="sr-only">
                            Milyen munkát keresel?
                        </label>
                        <input
                            id="job-search"
                            type="search"
                            autoComplete="off"
                            placeholder="Pozíció vagy kulcsszó..."
                            className="min-w-0 w-full bg-transparent outline-none text-neutral-800 placeholder:text-neutral-400 text-sm md:text-base"
                            value={props.searchQuery || ''}
                            onChange={(e) => props.setSearchQuery(e.target.value)}
                        />

                        {props.searchQuery && (
                            <button
                                type="button"
                                aria-label="Keresés törlése"
                                title="Keresés törlése"
                                onClick={() => props.setSearchQuery('')}
                                className="size-8 rounded-lg text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 transition shrink-0"
                            >
                                <PiXBold className="m-auto text-sm" />
                            </button>
                        )}
                    </div>

                    <button
                        type="button"
                        aria-label="Szűrők"
                        title="Szűrők"
                        className={`size-14 rounded-xl flex items-center justify-center transition shrink-0 ${
                            props.showFilter
                                ? 'bg-lime-500 text-white'
                                : 'bg-white/90 text-neutral-500 hover:bg-white'
                        }`}
                        onClick={() => props.setShowFilter(!props.showFilter)}
                    >
                        <PiFunnelSimple className="text-xl" />
                    </button>

                    <button
                        type="button"
                        aria-label="Kedvenc diákmunkák"
                        title="Kedvenc diákmunkák"
                        className={`size-14 rounded-xl flex items-center justify-center transition shrink-0 ${
                            props.showFavorites
                                ? 'bg-red-500 text-white'
                                : 'bg-white/90 text-neutral-500 hover:bg-white'
                        }`}
                        onClick={() => props.setShowFavorites(!props.showFavorites)}
                    >
                        <PiHeartDuotone className="text-xl" />
                    </button>
                </div>

                <div className="px-2 pt-2 text-right text-xs text-white/60" aria-live="polite">
                    {props.isLoading ? 'Keresés…' : `${props.resultCount} találat`}
                </div>
                
            </div>
        </div>
    );
}

const predefinedJobAreas = [
  { id: 1, icon: "/assets/images/icons/Webicon125.svg", border: "border-sky-400" },	
  { id: 2, icon: "/assets/images/icons/Webicon126.svg", border: "border-sky-400" },	
  { id: 3, icon: "/assets/images/icons/Webicon127.svg", border: "border-lime-400" },
  { id: 4, icon: "/assets/images/icons/Webicon128.svg", border: "border-orange-400" },	
  { id: 5, icon: "/assets/images/icons/Webicon129.svg", border: "border-lime-400" },
  { id: 6, icon: "/assets/images/icons/Webicon130.svg", border: "border-lime-400" },
  { id: 7, icon: "/assets/images/icons/Webicon131.svg", border: "border-orange-400" },
];

const extraTiles = [
  { key: "latest", name: "Legújabb", color: "yellow", icon: "/assets/images/icons/Webicon132.svg", border: "border-orange-400" },
  { key: "u18",    name: "18 év alatt is", color: "blue", icon: "/assets/images/icons/Webicon133.svg", border: "border-sky-400" },
];

function FilterSidebar({
  isOpen,
  onClose,
  onApplyFilter,
  selectedCountyId,
  onCountyChange,
  selectedSettlementId,
  onSettlementChange,
  selectedJobAreaIds,
  onJobAreasChange,
  filterUnder18,
  setFilterUnder18,
  filterLatest,
  setFilterLatest,
}) {
    const { getCombo } = useConfig();
    const counties = getCombo("county") || [];
    const allSettlements = getCombo("settlement") || [];
    const jobAreas = (getCombo("jobArea") || []).map((ja) => ({
        ...predefinedJobAreas.find((p) => p.id === ja.id),
        ...ja,
    }));

    const toggleJobArea = (id) => {
        const next = selectedJobAreaIds.includes(id)
            ? selectedJobAreaIds.filter((x) => x !== id)
            : [...selectedJobAreaIds, id];

        onJobAreasChange(next);
    };

    const selectedCounty = counties.find((c) => Number(c.id) === Number(selectedCountyId));

    const settlements =
        selectedCountyId == null
            ? allSettlements.filter((s) => s.primary === true)
            : allSettlements.filter(
                (s) =>
                    Number(s.countyID) === Number(selectedCounty?.countyID) &&
                    s.primary === true
            );

    useEffect(() => {
        if (!isOpen) return;

        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    return (
        <>
            <div
                className={`
                    fixed inset-0 z-30 bg-black/30 backdrop-blur-[1px]
                    transition-opacity duration-300 ease-in-out
                    ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                `}
                onClick={onClose}
            />

            <div
                className={`
                    fixed left-2 top-2 z-40 w-80 max-w-[90vw] rounded-2xl border border-neutral-400 dark:border-neutral-700/70
                    dark:bg-neutral-900/95 shadow-2xl backdrop-blur-sm p-4 overflow-y-auto flex flex-col gap-4
                    transform transition-all duration-300 ease-in-out
                    ${isOpen ? 'translate-x-0 opacity-100' : '-translate-x-[110%] opacity-0'}
                `}
            >
                <div className='flex items-center justify-between gap-4'>
                    <div className='font-display font-bold text-white text-xl'>
                        Szűrők
                    </div>

                    <button
                        type='button'
                        onClick={onClose}
                        className='rounded-xl bg-white/10 p-2 text-white hover:bg-white/20 transition'
                    >
                        <PiXBold className='text-xl' />
                    </button>
                </div>
                
                <div className='max-h-[70vh] overflow-y-auto flex flex-col gap-4'>
                    <label className="block text-white/80 text-sm">Megye</label>
                    <div className="group relative rounded-xl p-[3px] bg-gradient-to-r from-lime-400 to-green-500">
                        <div className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-200 group-hover:opacity-100 bg-gradient-to-r from-sky-400 to-blue-600" />

                        <div className="relative rounded-[10px] bg-neutral-900">
                            <select
                                className="w-full rounded-[10px] bg-black text-white px-4 py-3 pr-10 outline-none appearance-none cursor-pointer"
                                value={selectedCountyId ?? ""}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    onCountyChange(v === "" ? null : Number(v));
                                }}
                            >
                                    <option value="">Összes megye</option>
                                    {counties.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                            </select>

                            <PiCaretDownBold className="text-xl pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-lime-300 group-hover:text-blue-300 transition-colors" />
                        </div>
                    </div>

                    <label className="block text-white/80 text-sm">Település</label>
                    <div className="group relative rounded-xl p-[3px] bg-gradient-to-r from-orange-400 to-yellow-400">
                        <div className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-200 group-hover:opacity-100 bg-gradient-to-r from-blue-400 to-blue-600" />
                        <div className="relative rounded-[10px] bg-neutral-900">
                            <select
                                className="w-full rounded-[10px] bg-black text-white px-4 py-3 pr-10 outline-none appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                value={selectedSettlementId ?? ""}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    onSettlementChange(v === "" ? null : Number(v));
                                }}
                            >
                                <option value="">Összes település</option>
                                {settlements.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.name}
                                </option>
                                ))}
                            </select>

                            <PiCaretDownBold className="text-xl pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-orange-300 group-hover:text-blue-300 transition-colors" />
                        </div>
                    </div>
                    
                    <label className="block text-white/80 text-sm">Munkakör</label>
                    <div className="grid grid-cols-3 gap-3">
                        {jobAreas.map((ja) => {
                            const active = selectedJobAreaIds.includes(ja.id);

                            return (
                                <button
                                    key={ja.id}
                                    type="button"
                                    onClick={() => toggleJobArea(ja.id)}
                                    className={`
                                        flex flex-col items-center justify-center aspect-square rounded-2xl p-1.5
                                        transition-all duration-200 hover:ring hover:ring-2 hover:ring-white/20
                                        ${active ? `gradient-${ja.color}` : `bg-neutral-900 border-2 ${ja.border}`}
                                    `}
                                >
                                    {!!ja.icon && (
                                        <img
                                            src={ja.icon}
                                            alt={ja.name}
                                            className={`w-8 h-8 mb-1 ${active ? "filter brightness-0 invert" : ""}`}
                                        />
                                    )}

                                    <span className={`text-[10px] font-display font-bold text-center leading-[1.1] ${active ? "text-white" : "text-white/90"}`} >
                                        {ja.name}
                                    </span>
                                </button>
                            );
                        })}

                        {extraTiles.map((t) => {
                            const active = t.key === "u18" ? filterUnder18 : t.key === "latest" ? filterLatest : false;

                            const onClick = t.key === "u18" ? () => setFilterUnder18((v) => !v) : t.key === "latest" ? () => setFilterLatest((v) => !v) : () => {};

                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={onClick}
                                    className={`
                                        flex flex-col items-center justify-center aspect-square rounded-2xl p-1.5
                                        transition-all duration-200 hover:ring hover:ring-2 hover:ring-white/20
                                        ${active ? `gradient-${t.color}` : `bg-neutral-900 border-2 ${t.border}`}
                                    `}
                                >
                                    <img
                                        src={t.icon}
                                        alt={t.name}
                                        className={`w-8 h-8 mb-1 ${active ? "filter brightness-0 invert" : ""}`}
                                    />
                                    <span className={`text-[10px] font-display font-bold text-center leading-[1.1] ${active ? "text-white" : "text-white/90"}`}>
                                        {t.name}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className='flex gap-4'>
                    <button
                        type='button'
                        className='w-full rounded-xl p-3 font-display text-white gradient-green hover:bg-gradient-to-r from-green-600 to-lime-600'
                        onClick={onApplyFilter}
                    >
                        Keresés
                    </button>

                    <button
                        type="button"
                        className="rounded-xl py-2 px-3 font-display text-white gradient-red hover:bg-gradient-to-r from-red-600 to-red-800"
                        onClick={() => {
                            onCountyChange(null);
                            onSettlementChange(null);
                            onJobAreasChange([]);
                            setFilterUnder18(false);
                            setFilterLatest(false);
                        }}
                    >
                        <PiTrashDuotone className='text-2xl'/>
                    </button>
                </div>
            </div>
        </>
    );
}
